import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  AlertTriangle, Activity, FileText, CheckCircle, Shield,
  Users, Clock, DollarSign, TrendingDown, Download, Search,
  BarChart3, Calendar, AlertCircle, ClipboardList, FileBarChart,
  ChevronRight, ArrowUpRight, ArrowDownRight
} from "lucide-react";

const QAPIFallLogPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('log');
  const [loading, setLoading] = useState(false);
  const [fallLog, setFallLog] = useState({ falls: [], summary: {} });
  const [metrics, setMetrics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [rcaAlerts, setRcaAlerts] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedRCA, setSelectedRCA] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    patient_id: '',
    severity: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    if (!authLoading && (!user || !['admin', 'administrator', 'staff'].includes(user.role))) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    loadFallLog();
    loadMetrics();
    loadTrends();
    loadRCAAlerts();
  }, []);

  const loadFallLog = async () => {
    try {
      const params = {};
      if (filters.patient_id) params.patient_id = filters.patient_id;
      if (filters.severity) params.severity = filters.severity;
      
      const response = await api.get('/ehr/qapi-falls/log', { params });
      setFallLog(response.data);
    } catch (error) {
      console.error("Failed to load fall log:", error);
    }
  };

  const loadMetrics = async () => {
    try {
      const response = await api.get('/ehr/qapi-falls/metrics');
      setMetrics(response.data);
    } catch (error) {
      console.error("Failed to load metrics:", error);
    }
  };

  const loadTrends = async () => {
    try {
      const response = await api.get('/ehr/qapi-falls/trends');
      setTrends(response.data);
    } catch (error) {
      console.error("Failed to load trends:", error);
    }
  };

  const loadRCAAlerts = async () => {
    try {
      const response = await api.get('/ehr/qapi-falls/alerts/rca-required');
      setRcaAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Failed to load RCA alerts:", error);
    }
  };

  const loadRCADetails = async (patientId) => {
    try {
      const response = await api.get(`/ehr/qapi-falls/rca/${patientId}`);
      setSelectedRCA(response.data);
    } catch (error) {
      console.error("Failed to load RCA:", error);
    }
  };

  const handleExportQAPI = async () => {
    setLoading(true);
    try {
      const response = await api.post('/ehr/qapi-falls/export/qapi', {
        start_date: filters.start_date || '2026-01-01',
        end_date: filters.end_date || new Date().toISOString().split('T')[0],
        include_patient_details: true,
        include_rca: true,
        include_cost_analysis: true,
        generated_by: user?.name || 'Staff'
      });
      
      toast.success("QAPI Report Generated Successfully!");
      setShowExportModal(false);
      
      // In production, would trigger download
      console.log("QAPI Report:", response.data);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to generate QAPI report");
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'major': return 'bg-red-100 text-red-700';
      case 'moderate': return 'bg-orange-100 text-orange-700';
      case 'minor': return 'bg-yellow-100 text-yellow-700';
      case 'no_injury': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-purple-600" />
                QAPI Fall Tracking
              </h1>
              <p className="text-muted-foreground mt-1">
                Agency-wide fall tracking, analytics, and survey-ready reporting
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowExportModal(true)}>
                <Download className="w-4 h-4 mr-2" /> Export QAPI Report
              </Button>
              <Button variant="outline" onClick={() => navigate('/ehr-compliance')}>
                Back to EHR
              </Button>
            </div>
          </div>
        </div>

        {/* RCA Alerts Banner */}
        {rcaAlerts.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-red-700">Root Cause Analysis Required</p>
                {rcaAlerts.map((alert, idx) => (
                  <div key={idx} className="mt-2 flex items-center justify-between">
                    <div>
                      <span className="font-medium">{alert.patient_name}</span>
                      <span className="text-sm text-red-600 ml-2">
                        {alert.fall_count} falls in {alert.period_days} days
                      </span>
                    </div>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => loadRCADetails(alert.patient_id)}
                    >
                      View RCA
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Falls</p>
                  <p className="text-2xl font-bold">{fallLog.summary.total_falls || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Injury Rate</p>
                  <p className="text-2xl font-bold">{fallLog.summary.injury_rate || 0}%</p>
                </div>
                <Activity className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg MD Response</p>
                  <p className="text-2xl font-bold">{fallLog.summary.avg_time_to_md_notification || 0} min</p>
                </div>
                <Clock className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Cost</p>
                  <p className="text-2xl font-bold">${fallLog.summary.total_cost || 0}</p>
                </div>
                <DollarSign className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">RCA Required</p>
                  <p className="text-2xl font-bold">{rcaAlerts.length}</p>
                </div>
                <ClipboardList className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="log" data-testid="tab-log">
              <FileText className="w-4 h-4 mr-2" />
              Fall Log
            </TabsTrigger>
            <TabsTrigger value="metrics" data-testid="tab-metrics">
              <BarChart3 className="w-4 h-4 mr-2" />
              Metrics & Analysis
            </TabsTrigger>
            <TabsTrigger value="trends" data-testid="tab-trends">
              <TrendingDown className="w-4 h-4 mr-2" />
              Trends
            </TabsTrigger>
            <TabsTrigger value="rca" data-testid="tab-rca">
              <ClipboardList className="w-4 h-4 mr-2" />
              Root Cause Analysis
            </TabsTrigger>
          </TabsList>

          {/* Fall Log Tab */}
          <TabsContent value="log">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Agency-Wide Fall Log</CardTitle>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Filter by patient..."
                      className="w-48"
                      value={filters.patient_id}
                      onChange={(e) => setFilters(prev => ({ ...prev, patient_id: e.target.value }))}
                    />
                    <Button size="sm" onClick={loadFallLog}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium">Date/Time</th>
                        <th className="text-left p-3 font-medium">Patient</th>
                        <th className="text-left p-3 font-medium">Location</th>
                        <th className="text-left p-3 font-medium">Severity</th>
                        <th className="text-left p-3 font-medium">MD Response</th>
                        <th className="text-left p-3 font-medium">DME Ordered</th>
                        <th className="text-left p-3 font-medium">Cost</th>
                        <th className="text-left p-3 font-medium">RCA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fallLog.falls.map((fall, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50">
                          <td className="p-3">
                            <div>{fall.incident_date}</div>
                            <div className="text-sm text-muted-foreground">{fall.incident_time}</div>
                          </td>
                          <td className="p-3 font-medium">{fall.patient_name}</td>
                          <td className="p-3 capitalize">{fall.location.replace('_', ' ')}</td>
                          <td className="p-3">
                            <Badge className={getSeverityColor(fall.severity)}>
                              {fall.severity.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="p-3">
                            {fall.time_to_md_notification ? (
                              <span className={fall.time_to_md_notification <= 15 ? 'text-green-600' : 'text-amber-600'}>
                                {fall.time_to_md_notification} min
                              </span>
                            ) : '-'}
                          </td>
                          <td className="p-3">
                            {fall.dme_ordered.length > 0 ? (
                              <Badge variant="outline">{fall.dme_ordered.length} items</Badge>
                            ) : '-'}
                          </td>
                          <td className="p-3 font-medium">${fall.cost_incurred}</td>
                          <td className="p-3">
                            {fall.rca_required ? (
                              <Badge className={
                                fall.rca_status === 'completed' ? 'bg-green-100 text-green-700' :
                                fall.rca_status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }>
                                {fall.rca_status.replace('_', ' ')}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">N/A</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Metrics Tab */}
          <TabsContent value="metrics">
            {metrics && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Location Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Falls by Location</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(metrics.falls_by_location || {}).map(([location, count]) => (
                        <div key={location} className="flex items-center justify-between">
                          <span className="capitalize">{location.replace('_', ' ')}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 h-3 bg-slate-100 rounded-full">
                              <div 
                                className="h-3 bg-blue-500 rounded-full"
                                style={{ width: `${(count / metrics.total_falls) * 100}%` }}
                              />
                            </div>
                            <span className="font-bold w-8">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Time of Day Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Falls by Time of Day</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(metrics.falls_by_time_of_day || {}).map(([time, count]) => (
                        <div key={time} className="flex items-center justify-between">
                          <span className="capitalize">{time} (
                            {time === 'night' ? '12am-6am' :
                             time === 'morning' ? '6am-12pm' :
                             time === 'afternoon' ? '12pm-6pm' : '6pm-12am'}
                          )</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 h-3 bg-slate-100 rounded-full">
                              <div 
                                className={`h-3 rounded-full ${
                                  time === 'night' ? 'bg-indigo-500' :
                                  time === 'morning' ? 'bg-amber-500' :
                                  time === 'afternoon' ? 'bg-orange-500' : 'bg-purple-500'
                                }`}
                                style={{ width: `${(count / metrics.total_falls) * 100}%` }}
                              />
                            </div>
                            <span className="font-bold w-8">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Response Time Metrics */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Response Time Analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex justify-between mb-1">
                          <span>MD Notification</span>
                          <span className="font-bold">{metrics.avg_time_to_intervention?.md_notification} min</span>
                        </div>
                        <div className="text-xs text-muted-foreground">Target: ≤30 min</div>
                        <Badge className={
                          metrics.avg_time_to_intervention?.md_notification <= 30 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }>
                          {metrics.avg_time_to_intervention?.md_notification <= 30 ? 'TARGET MET' : 'NEEDS IMPROVEMENT'}
                        </Badge>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex justify-between mb-1">
                          <span>DME Order</span>
                          <span className="font-bold">{metrics.avg_time_to_intervention?.dme_order} min</span>
                        </div>
                        <div className="text-xs text-muted-foreground">Target: ≤60 min</div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex justify-between mb-1">
                          <span>Care Plan Update</span>
                          <span className="font-bold">{metrics.avg_time_to_intervention?.care_plan_update} min</span>
                        </div>
                        <div className="text-xs text-muted-foreground">Target: ≤240 min (same day)</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Cost Analysis */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Cost Analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-green-50 rounded-lg text-center">
                          <p className="text-sm text-muted-foreground">Total Cost</p>
                          <p className="text-2xl font-bold text-green-700">
                            ${metrics.cost_analysis?.total_cost}
                          </p>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg text-center">
                          <p className="text-sm text-muted-foreground">Avg Per Fall</p>
                          <p className="text-2xl font-bold text-blue-700">
                            ${metrics.cost_analysis?.avg_cost_per_fall}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium mb-2">Cost Breakdown</p>
                        {Object.entries(metrics.cost_analysis?.breakdown || {}).map(([category, cost]) => (
                          <div key={category} className="flex justify-between py-1 border-b">
                            <span className="capitalize">{category.replace('_', ' ')}</span>
                            <span className="font-medium">${cost.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Multiple Falls Patients */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      Patients with Multiple Falls
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {metrics.patients_with_multiple_falls?.length > 0 ? (
                      <div className="space-y-3">
                        {metrics.patients_with_multiple_falls.map((patient, idx) => (
                          <div key={idx} className="p-3 border rounded-lg flex items-center justify-between bg-red-50">
                            <div>
                              <span className="font-medium">{patient.patient_name}</span>
                              <Badge className="ml-2 bg-red-100 text-red-700">
                                {patient.count} falls
                              </Badge>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => loadRCADetails(patient.patient_id)}
                            >
                              View RCA <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-4">
                        No patients with multiple falls in this period
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends">
            {trends && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>6-Month Fall Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Trend indicators */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className={`p-4 rounded-lg ${
                          trends.trend_analysis?.fall_rate_trend === 'decreasing' 
                            ? 'bg-green-50' : 'bg-amber-50'
                        }`}>
                          <div className="flex items-center gap-2">
                            {trends.trend_analysis?.fall_rate_trend === 'decreasing' ? (
                              <ArrowDownRight className="w-5 h-5 text-green-600" />
                            ) : (
                              <ArrowUpRight className="w-5 h-5 text-amber-600" />
                            )}
                            <span className="font-medium">Fall Rate</span>
                          </div>
                          <p className={`text-lg font-bold ${
                            trends.trend_analysis?.fall_rate_trend === 'decreasing' 
                              ? 'text-green-700' : 'text-amber-700'
                          }`}>
                            {trends.trend_analysis?.fall_rate_trend?.toUpperCase()}
                          </p>
                        </div>
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <span className="font-medium">Injury Rate</span>
                          <p className="text-lg font-bold text-blue-700">
                            {trends.trend_analysis?.injury_rate_trend?.toUpperCase()}
                          </p>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg">
                          <span className="font-medium">Intervention Impact</span>
                          <p className="text-sm font-bold text-purple-700">
                            {trends.trend_analysis?.interventions_impact}
                          </p>
                        </div>
                      </div>

                      {/* Monthly data */}
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-3 font-medium">Month</th>
                              <th className="text-center p-3 font-medium">Falls</th>
                              <th className="text-center p-3 font-medium">Injuries</th>
                              <th className="text-right p-3 font-medium">Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trends.monthly_data?.map((month, idx) => (
                              <tr key={idx} className="border-b">
                                <td className="p-3">{month.month}</td>
                                <td className="p-3 text-center">
                                  <Badge variant="outline">{month.falls}</Badge>
                                </td>
                                <td className="p-3 text-center">
                                  <Badge className={
                                    month.injuries === 0 ? 'bg-green-100 text-green-700' :
                                    month.injuries <= 2 ? 'bg-amber-100 text-amber-700' :
                                    'bg-red-100 text-red-700'
                                  }>
                                    {month.injuries}
                                  </Badge>
                                </td>
                                <td className="p-3 text-right font-medium">${month.cost}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Recommendations */}
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="font-semibold text-blue-700 mb-2">Recommendations</p>
                        {trends.recommendations?.map((rec, idx) => (
                          <p key={idx} className="text-sm text-blue-600 mb-1">• {rec}</p>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* RCA Tab */}
          <TabsContent value="rca">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* RCA Alerts */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    RCA Required
                  </CardTitle>
                  <CardDescription>
                    Patients with 2+ falls in 30 days require Root Cause Analysis
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {rcaAlerts.length > 0 ? (
                    <div className="space-y-3">
                      {rcaAlerts.map((alert, idx) => (
                        <div key={idx} className="p-4 border rounded-lg bg-red-50 border-red-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">{alert.patient_name}</span>
                            <Badge className="bg-red-100 text-red-700">{alert.priority}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">Falls:</span>
                              <span className="ml-1 font-medium">{alert.fall_count}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Period:</span>
                              <span className="ml-1 font-medium">{alert.period_days} days</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Most Recent:</span>
                              <span className="ml-1 font-medium">{alert.most_recent_fall}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Trend:</span>
                              <span className="ml-1 font-medium text-red-600">{alert.severity_trend}</span>
                            </div>
                          </div>
                          <div className="mt-3 flex gap-2">
                            <Button 
                              size="sm"
                              onClick={() => loadRCADetails(alert.patient_id)}
                            >
                              {alert.rca_status === 'in_progress' ? 'Continue RCA' : 'Start RCA'}
                            </Button>
                            <Badge className={
                              alert.rca_status === 'completed' ? 'bg-green-100 text-green-700' :
                              alert.rca_status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              {alert.rca_status.replace('_', ' ')}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                      <p className="text-muted-foreground">No RCA required at this time</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* RCA Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Root Cause Analysis Details</CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedRCA && selectedRCA.rca_id ? (
                    <div className="space-y-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-semibold">{selectedRCA.patient_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {selectedRCA.fall_count} falls in {selectedRCA.period_days} days
                        </p>
                      </div>

                      <div>
                        <p className="font-medium mb-2">Root Causes Identified</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedRCA.root_causes?.map((cause, idx) => (
                            <Badge key={idx} variant="outline" className="capitalize">
                              {cause.replace('_', ' ')}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="font-medium mb-2">Contributing Factors</p>
                        <ul className="text-sm space-y-1">
                          {selectedRCA.contributing_factors?.map((factor, idx) => (
                            <li key={idx} className="text-muted-foreground">• {factor}</li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium mb-2">Corrective Actions</p>
                        <div className="space-y-2">
                          {selectedRCA.corrective_actions?.map((action, idx) => (
                            <div key={idx} className="p-2 border rounded flex items-center justify-between">
                              <div>
                                <p className="text-sm">{action.action}</p>
                                <p className="text-xs text-muted-foreground">
                                  {action.responsible} • Due: {action.due_date}
                                </p>
                              </div>
                              <Badge className={
                                action.status === 'completed' ? 'bg-green-100 text-green-700' :
                                'bg-amber-100 text-amber-700'
                              }>
                                {action.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="font-medium mb-2">Preventive Measures</p>
                        <ul className="text-sm space-y-1">
                          {selectedRCA.preventive_measures?.map((measure, idx) => (
                            <li key={idx} className="text-green-600">✓ {measure}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground opacity-30 mb-2" />
                      <p className="text-muted-foreground">Select a patient to view RCA details</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* QAPI Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileBarChart className="w-5 h-5 text-purple-600" />
              Export QAPI Fall Report
            </DialogTitle>
            <DialogDescription>
              Generate a survey-ready fall prevention PI project report
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-700 mb-2">Report Will Include:</p>
              <ul className="text-sm text-blue-600 space-y-1">
                <li>✓ Executive summary with benchmarks</li>
                <li>✓ Fall incident details with timestamps</li>
                <li>✓ Intervention tracking and response times</li>
                <li>✓ Root Cause Analysis summaries</li>
                <li>✓ Cost analysis and ROI</li>
                <li>✓ Quality improvement actions</li>
                <li>✓ Compliance attestation</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleExportQAPI} disabled={loading}>
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Generate Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QAPIFallLogPage;
